import { useEffect, useState } from 'react';
import { http, crestHttp } from '../api/client.js';
import type { SitemapNode } from './AppLayout.js';
import { normalizePortalAppDisplayName } from './app-display-name.js';

/** /me/apps 行（与 AppSwitcher 使用同一端点） */
export interface PortalAppItem {
  appid: string;
  name: string;
  icon: string | null;
  display_name_zh: string;
  display_name_en: string;
  is_default?: boolean;
}

/**
 * 统一门户菜单数据源：用户可访问的应用列表 + 每个应用的 RBAC 过滤后 sitemap。
 * 应用成为一级菜单，原各应用一级菜单降为二级（M-portal 导航形态）。
 */
export function usePortalSitemaps(
  enabled: boolean,
  sitemapLoader?: (appName: string) => Promise<SitemapNode[]>,
): {
  portalApps: PortalAppItem[];
  portalSitemaps: Record<string, SitemapNode[]>;
} {
  const [portalApps, setPortalApps] = useState<PortalAppItem[]>([]);
  const [portalSitemaps, setPortalSitemaps] = useState<Record<string, SitemapNode[]>>({});

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    crestHttp
      .get<PortalAppItem[]>('/me/apps')
      .then((r) => {
        if (cancelled) return;
        const rows = r.data.map(normalizePortalAppDisplayName);
        setPortalApps(rows);
        for (const app of rows) {
          const request = sitemapLoader
            ? sitemapLoader(app.name)
            : http
                .get<SitemapNode[]>(`/apps/${app.name}/sitemap`, { params: { t: Date.now() } })
                .then((res) => res.data);
          request
            .then((items) => {
              if (cancelled) return;
              setPortalSitemaps((prev) => ({ ...prev, [app.name]: items }));
            })
            .catch(() => {
              if (cancelled) return;
              setPortalSitemaps((prev) => ({ ...prev, [app.name]: [] }));
            });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled, sitemapLoader]);

  return { portalApps, portalSitemaps };
}
